import type { Routes } from '@angular/router';
import { protectedDashboardRoute } from '@core/routing/protected-dashboard.route';
import { protectedDefaultRoute } from '@core/routing/protected-default.route';
import { OfferingContextStore } from '@features/launchpad/data-access/offering-context.store';

export const routes: Routes = [
  {
    path: 'not-found',
    data: { title: 'Page Not Found' },
    loadComponent: () =>
      import('@features/errors/components/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
  },
  {
    path: 'forbidden',
    data: { title: 'Forbidden' },
    loadComponent: () =>
      import('@features/errors/components/forbidden/forbidden.component').then(
        (m) => m.ForbiddenComponent,
      ),
  },
  {
    path: 'server-error',
    data: { title: 'Server Error' },
    loadComponent: () =>
      import('@features/errors/components/server-error/server-error.component').then(
        (m) => m.ServerErrorComponent,
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('@layouts/default-layout/default-layout.component').then(
        (m) => m.DefaultLayoutComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('@features/landing-page/components/landing-page/landing-page.component').then(
            (m) => m.LandingPageComponent,
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('@features/landing-page/components/landing-page-title/landing-page-title.component').then(
            (m) => m.LandingPageTitleComponent,
          ),
        outlet: 'title',
      },
    ],
  },
  protectedDefaultRoute('tokenisation', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/tokenisation-page/components/tokenisation-page/tokenisation-page.component').then(
          (m) => m.TokenisationPageComponent,
        ),
    },
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/tokenisation-page/components/tokenisation-title/tokenisation-title.component').then(
          (m) => m.TokenisationTitleComponent,
        ),
      outlet: 'title',
    },
  ]),
  protectedDefaultRoute(
    'launchpad',
    [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('@features/launchpad/components/offering-list/offering-list.component').then(
            (m) => m.OfferingListComponent,
          ),
      },
      {
        path: ':projectId',
        providers: [OfferingContextStore],
        children: [
          {
            path: 'invest',
            loadComponent: () =>
              import('@features/launchpad/components/invest-flow/invest-flow.component').then(
                (m) => m.InvestFlowComponent,
              ),
          },
          {
            path: '',
            loadComponent: () =>
              import('@features/launchpad/components/offering-detail/offering-detail.component').then(
                (m) => m.OfferingDetailComponent,
              ),
          },
        ],
      },
    ],
    { layout: 'app' },
  ),
  protectedDashboardRoute('dashboard', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/dashboard/components/dashboard-home/dashboard-home.component').then(
          (m) => m.DashboardHomeComponent,
        ),
      data: { title: 'Dashboard' },
    },
  ]),
  protectedDashboardRoute('investments', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/launchpad/components/my-investments/my-investments.component').then(
          (m) => m.MyInvestmentsComponent,
        ),
      data: { title: 'My investments' },
    },
  ]),
  protectedDashboardRoute('projects', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/dashboard/components/projects/projects.component').then(
          (m) => m.ProjectsComponent,
        ),
      data: { title: 'Projects' },
    },
    {
      path: 'create-project',
      pathMatch: 'full',
      redirectTo: '/project-details',
    },
    {
      path: 'edit-project/:id',
      pathMatch: 'full',
      redirectTo: '/project-details/:id',
    },
    {
      path: 'view-project/:id',
      pathMatch: 'full',
      redirectTo: '/project-details/:id/view',
    },
  ]),
  protectedDashboardRoute('profile-details', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/profile/components/profile-details/profile-details.component').then(
          (m) => m.ProfileDetailsComponent,
        ),
      data: { title: 'Profile' },
    },
  ]),
  protectedDashboardRoute('project-details', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/projects/components/project-details/project-details.component').then(
          (m) => m.ProjectDetailsComponent,
        ),
      data: { title: 'Create Project' },
    },
  ]),
  protectedDashboardRoute('project-details/:id', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/projects/components/project-details/project-details.component').then(
          (m) => m.ProjectDetailsComponent,
        ),
      data: { title: 'Edit Project' },
    },
  ]),
  protectedDashboardRoute('project-details/:id/view', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/projects/components/project-details/project-details.component').then(
          (m) => m.ProjectDetailsComponent,
        ),
      data: { title: 'Project details' },
    },
  ]),
  protectedDashboardRoute('token-details', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/tokens/components/token-details/token-details.component').then(
          (m) => m.TokenDetailsComponent,
        ),
      data: { title: 'Create Token' },
    },
    {
      path: ':id',
      loadComponent: () =>
        import('@features/tokens/components/token-details/token-details.component').then(
          (m) => m.TokenDetailsComponent,
        ),
      data: { title: 'Token configuration' },
    },
  ]),
  protectedDashboardRoute('manage-tokens', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/tokens/components/manage-tokens/manage-tokens.component').then(
          (m) => m.ManageTokensComponent,
        ),
      data: { title: 'Manage Tokens' },
    },
  ]),
  protectedDashboardRoute('contract-tools', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import(
          '@features/contract-tools/components/contract-tools-home/contract-tools-home.component'
        ).then((m) => m.ContractToolsHomeComponent),
      data: { title: 'On-chain Tools' },
    },
    {
      path: 'token',
      loadComponent: () =>
        import(
          '@features/contract-tools/components/token-inspector/token-inspector.component'
        ).then((m) => m.TokenInspectorComponent),
      data: { title: 'Token Inspector' },
    },
    {
      path: 'ido',
      loadComponent: () =>
        import(
          '@features/contract-tools/components/ido-inspector/ido-inspector.component'
        ).then((m) => m.IdoInspectorComponent),
      data: { title: 'IDO Inspector' },
    },
    {
      path: 'wallet',
      loadComponent: () =>
        import(
          '@features/contract-tools/components/wallet-inspector/wallet-inspector.component'
        ).then((m) => m.WalletInspectorComponent),
      data: { title: 'Wallet Inspector' },
    },
  ]),
  protectedDashboardRoute('token-distribution', [
    {
      path: '',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/token-distribution/components/distribution-list/distribution-list.component').then(
          (m) => m.DistributionListComponent,
        ),
      data: { title: 'Token Distribution' },
    },
    {
      path: 'create',
      pathMatch: 'full',
      loadComponent: () =>
        import('@features/token-distribution/components/create-distribution/create-distribution.component').then(
          (m) => m.CreateDistributionComponent,
        ),
      data: { title: 'Create collection' },
    },
    {
      path: ':programId/streams/new',
      loadComponent: () =>
        import('@features/token-distribution/components/create-stream/create-stream.component').then(
          (m) => m.CreateStreamComponent,
        ),
      data: { title: 'Create payment stream' },
    },
    {
      path: ':programId',
      loadComponent: () =>
        import('@features/token-distribution/components/manage-distribution/manage-distribution.component').then(
          (m) => m.ManageDistributionComponent,
        ),
      data: { title: 'Manage payment stream' },
    },
  ]),
  {
    path: '**',
    redirectTo: '/not-found',
  },
];
